import * as am4charts from '@amcharts/amcharts4/charts';
import * as am4core from '@amcharts/amcharts4/core';
import am4themes_animated from '@amcharts/amcharts4/themes/animated';
import {faChartBar, faTable} from '@fortawesome/pro-light-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {push} from 'connected-react-router';
import {LocationDescriptorObject} from 'history';

import {withTranslation, WithTranslation} from 'react-i18next';
import {connect} from 'react-redux';
import {Dispatch} from 'redux';
import {Color} from "@amcharts/amcharts4/core";
import {DropdownOptionKey} from '../../../../components/widget-header-dropdown/dropdown-option-key';
import WidgetHeaderDropdown, {
    DropdownOption,
} from '../../../../components/widget-header-dropdown/widget-header-dropdown';
import WidgetTable, {WidgetTableColumnType} from '../../../../components/widget-table/widget-table';
import {ApplicationState} from '../../../../store';
import {fetchTopReusableTiresFittedByServiceProviderRequest,} from '../../../../store/analytics';
import {CurrencyInfo, FleetCustomerWithConfiguration, FleetType} from '../../../../store/fleet-customers';
import {ViewMode} from '../../../../types/view-mode';
import {generateUUID} from '../../../../utils/uuid-helpers';
import {ControlsSnapshot} from '../../dashboard-controls/controls-snapshot';
import {SingleChartWidgetBase} from '../single-chart-widget-base';
import {addStandardTooltipStyle, addStrokeToColumnChart, createXYChart} from '../utils/chart-utils';
import {hasInspectionPeriod} from '../utils/widget-helper';
import WidgetLoading from '../widget-loading';
import {
    buildReusableChartData,
    reusableNewTiresField,
    reusableTiresField,
    serviceProviderField
} from './chart-data-adapter';
import {buildReusableRequest} from './request-builder';
import styles from './top-reusable-tires-fitted-by-service-providers-widget.module.scss';
import {
    ReusableOrderType,
    TopReusableTiresFittedByServiceProvidersData,
    TopReusableTiresFittedByServiceProvidersRequest
} from "../../../../store/analytics/types/top-tires-fitted-by-service-providers-types";
import {colorBlue4, colorBlue8} from "../utils/chart-colors";

am4core.useTheme(am4themes_animated);

class TopReusableTiresFittedByServiceProvidersWidget extends SingleChartWidgetBase<AllProps, AllState, TopReusableTiresFittedByServiceProvidersData[]> {
    private chartId: string;

    private readonly OPTION_NEW_TIRES = {id: '1', key: DropdownOptionKey.ByNewTires};

    private readonly OPTION_REUSABLE_TIRES = {id: '2', key: DropdownOptionKey.ByReusableTires};

    constructor(props) {
        super(props);
        const {fleetCustomer} = this.props;
        this.chartId = generateUUID();

        this.state = {
            options: [this.OPTION_NEW_TIRES, this.OPTION_REUSABLE_TIRES],
            selectedOption: fleetCustomer.configuration.fleetType === FleetType.PayGo ? this.OPTION_NEW_TIRES : this.OPTION_REUSABLE_TIRES,
            viewMode: ViewMode.chart,
        };
    }

    public render(): JSX.Element {
        const {t, data, fleetCustomer} = this.props;
        const {selectedOption, options, viewMode} = this.state;

        const columns = [
            {
                title: 'Name',
                property: 'name',
                type: WidgetTableColumnType.upperText,
                width: '480px',
            },
            {
                title: '# New Tires',
                property: 'newCount',
                type: WidgetTableColumnType.number,
            }, {
                title: `# Reusable Tires`,
                property: 'reusableCount',
                type: WidgetTableColumnType.number,
            }
        ]

        return (
            <div className={styles.widgetContainer}>
                {data ? (
                    <div className={styles.chartView}>
                        <div className={styles.chartHeader}>
                            <div
                                className={styles.headerTitle}>{t('Top 20 (Reusable) Tires Fitted By Service Providers')}</div>
                            <div className={styles.interactions}>
                                {fleetCustomer.configuration.fleetType === FleetType.PayGo ? <WidgetHeaderDropdown
                                    options={options}
                                    selection={selectedOption}
                                    onSelectionChanged={(selection) => this.onSelect(selection)}
                                /> : ''}
                                <div className={styles.headerToggle} onClick={() => this.toggleViewMode()}>
                                    {viewMode === ViewMode.chart ? (
                                        <FontAwesomeIcon icon={faTable}/>
                                    ) : (
                                        <FontAwesomeIcon icon={faChartBar}/>
                                    )}
                                </div>
                            </div>
                        </div>
                        {viewMode === ViewMode.chart ? (
                            <div className={styles.chartContent}>
                                <div id={this.chartId} style={{width: '100%'}}/>
                            </div>
                        ) : (
                            <div className={styles.tableContent}>
                                <WidgetTable
                                    config={{columns}}
                                    data={data}
                                />
                            </div>
                        )}
                    </div>
                ) : (
                    <WidgetLoading/>
                )}
            </div>
        );
    }

    private onSelect(option: DropdownOption): void {
        this.setState({selectedOption: option, viewMode: ViewMode.chart}, () => {
            this.fetchData(this.props.controlsSnapshot);
        });
    }

    private toggleViewMode(): void {
        const {viewMode} = this.state;
        const updatedViewMode = viewMode === ViewMode.chart ? ViewMode.table : ViewMode.chart;
        this.setState({viewMode: updatedViewMode}, () => {
            if (this.state.viewMode === ViewMode.chart) {
                this.refreshChart();
            } else {
                this.disposeChart();
            }
        });
    }

    protected fetchData(snapshot: ControlsSnapshot): void {
        this.props.fetchTopReusableTiresFittedByServiceProvider(
            buildReusableRequest(this.props.controlsSnapshot, this.getOrderType(this.state.selectedOption)),
        );
    }

    protected validateControlsSnapshot(snapshot: ControlsSnapshot): boolean {
        return hasInspectionPeriod(snapshot);
    }

    protected createChart(data: TopReusableTiresFittedByServiceProvidersData[]): am4charts.XYChart {
        const chart = createXYChart(this.chartId);

        const categoryAxis = chart.yAxes.push(new am4charts.CategoryAxis());
        categoryAxis.dataFields.category = serviceProviderField;
        categoryAxis.renderer.grid.template.location = 0;
        categoryAxis.renderer.minGridDistance = 20;
        // categoryAxis.renderer.labels.template.truncate = false;
        categoryAxis.renderer.labels.template.wrap = true;
        categoryAxis.renderer.labels.template.maxWidth = 320;
        categoryAxis.renderer.labels.template.tooltipText = "{category}";

        const valueAxis = chart.xAxes.push(new am4charts.ValueAxis());
        valueAxis.min = 0;
        valueAxis.title.text = this.props.t('# Tires');
        chart.data = buildReusableChartData(data);
        this.addAllSeries(chart);

        chart.legend = new am4charts.Legend();
        chart.legend.position = 'bottom';
        chart.legend.reverseOrder = true;

        return chart;
    }

    private addAllSeries(chart: any): void {
        this.addSeries(chart,this.props.t('Reusable Tires'),am4core.color(colorBlue4),reusableTiresField);
        this.addSeries(chart,this.props.t('New Tires'),am4core.color(colorBlue8),reusableNewTiresField);
    }

    private addSeries(chart:any,seriesName:string,color:Color, valueXField:string):void{
        const series = chart.series.push(new am4charts.ColumnSeries());
        series.name = seriesName;
        series.dataFields.categoryY = serviceProviderField;
        series.dataFields.valueX = valueXField;
        series.columns.template.tooltipText = `{categoryY}: [bold]{valueX} ${
            seriesName
        }[/]`;
        series.columns.template.fillOpacity = 0.8;
        series.columns.template.maxHeight = 100;
        series.columns.template.adapter.add('fill', (fill, target) => color);

        const seriesValueLabel = series.bullets.push(new am4charts.LabelBullet());
        seriesValueLabel.label.text = '{valueX}';
        seriesValueLabel.label.horizontalCenter = 'left';
        seriesValueLabel.label.dx = 10;
        seriesValueLabel.label.hideOversized = false;
        seriesValueLabel.label.truncate = false;

        addStrokeToColumnChart(series);
        addStandardTooltipStyle(series);

    }

    private getOrderType(option: DropdownOption): ReusableOrderType {
        switch (option) {
            case this.OPTION_NEW_TIRES:
                return ReusableOrderType.NEW_TIRES;
            case this.OPTION_REUSABLE_TIRES:
                return ReusableOrderType.REUSABLE_TIRES;
            default:
                throw Error('Can not determine service provider order type');
        }
    }
}

const mapStateToProps = ({analytics, authentication}: ApplicationState) => ({
    data: analytics.topReusableTiresByServiceProvidersData,
    fleetCustomer: authentication.fleetCustomer,
    activeCurrency: authentication.fleetCustomer!.activeCurrency,
    fleetCustomerId: authentication.fleetCustomer?.id,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    fetchTopReusableTiresFittedByServiceProvider: (request: TopReusableTiresFittedByServiceProvidersRequest) =>
        dispatch(fetchTopReusableTiresFittedByServiceProviderRequest(request)),
    navigateTo: (location: LocationDescriptorObject) => dispatch(push(location)),
});

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(TopReusableTiresFittedByServiceProvidersWidget));

interface PropsFromState {
    data: TopReusableTiresFittedByServiceProvidersData[];
    fleetCustomer: FleetCustomerWithConfiguration;
    fleetCustomerId: string;
    activeCurrency: CurrencyInfo;
}

interface PropsFromDispatch {
    fetchTopReusableTiresFittedByServiceProvider: typeof fetchTopReusableTiresFittedByServiceProviderRequest;
    navigateTo: typeof push;
}

interface OwnProps {
    controlsSnapshot: ControlsSnapshot;
}

type AllProps = OwnProps & PropsFromState & PropsFromDispatch & WithTranslation;

interface OwnState {
    options: DropdownOption[];
    selectedOption: DropdownOption;
    viewMode: ViewMode;
}

type AllState = OwnState;
